/**
 * Déclarer candidat libre un élève de la liste officielle absent de KLASYS.
 *
 * Préremplie avec ce que dit la ligne (nom, prénoms, date et lieu de
 * naissance, genre si la colonne existe). Rien n'est créé ici : la
 * déclaration est gardée par l'étape de rapprochement, et le candidat libre
 * n'est créé qu'à l'enregistrement de l'import, dans la même transaction que
 * son résultat - sans frais d'examen attendus.
 */
import React, { useState } from 'react';
import { UserPlus, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/Label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import ChampSelect from '../ui/ChampSelect';

const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';
const CLASSES_CHAMP = 'h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const GENRES = [
  { valeur: 'M', libelle: 'Masculin' },
  { valeur: 'F', libelle: 'Féminin' }
];

/** Le formulaire de départ, tiré de la ligne du fichier. */
export const formulaireDepuisLigne = (fichier, existant = null) => existant || {
  nom: fichier?.nom || fichier?.nom_complet || '',
  prenom: fichier?.prenom || '',
  sexe: fichier?.sexe || '',
  date_naissance: fichier?.date_naissance || '',
  lieu_naissance: fichier?.lieu_naissance || '',
  telephone: ''
};

const DeclarationCandidatLibre = ({ fichier, initial, libelleLong, onFermer, onDeclarer }) => {
  const [formulaire, setFormulaire] = useState(() => formulaireDepuisLigne(fichier, initial));
  const [tente, setTente] = useState(false);
  const changer = (cle, valeur) => setFormulaire((precedent) => ({ ...precedent, [cle]: valeur }));

  const manque = {
    nom: !formulaire.nom.trim(),
    prenom: !formulaire.prenom.trim(),
    sexe: !formulaire.sexe,
    date_naissance: !formulaire.date_naissance
  };
  const incomplet = Object.values(manque).some(Boolean);

  const declarer = () => {
    setTente(true);
    if (incomplet) return;
    onDeclarer({
      ...formulaire,
      nom: formulaire.nom.trim(),
      prenom: formulaire.prenom.trim(),
      lieu_naissance: formulaire.lieu_naissance.trim(),
      telephone: formulaire.telephone.trim()
    });
  };

  const erreur = (cle, texte) => tente && manque[cle] && <p className="text-[11px] text-red-600">{texte}</p>;

  return (
    <Dialog open onOpenChange={(ouvert) => !ouvert && onFermer()}>
      <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 py-4 border-b">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#246990] text-white">
              <UserPlus size={20} />
            </span>
            Déclarer candidat libre
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Ligne {fichier?.ligne_fichier} de la liste du {libelleLong} : {fichier?.nom_complet}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="cl_nom" className="text-sm text-gray-700">Nom *</Label>
              <Input id="cl_nom" value={formulaire.nom} onChange={(e) => changer('nom', e.target.value)} maxLength={100} className={CLASSES_CHAMP} />
              {erreur('nom', 'Le nom est nécessaire.')}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cl_prenom" className="text-sm text-gray-700">Prénom(s) *</Label>
              <Input id="cl_prenom" value={formulaire.prenom} onChange={(e) => changer('prenom', e.target.value)} maxLength={100} className={CLASSES_CHAMP} />
              {erreur('prenom', 'Le prénom est nécessaire.')}
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-gray-700">Genre *</Label>
              <ChampSelect
                valeur={formulaire.sexe || undefined}
                onChange={(valeur) => changer('sexe', valeur)}
                options={GENRES}
                placeholder="Choisir"
                className="w-full"
              />
              {erreur('sexe', 'Le genre est nécessaire.')}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cl_date" className="text-sm text-gray-700">Date de naissance *</Label>
              <Input
                id="cl_date"
                type="date"
                value={formulaire.date_naissance}
                onChange={(e) => changer('date_naissance', e.target.value)}
                className={CLASSES_CHAMP}
              />
              {erreur('date_naissance', 'La date de naissance est nécessaire.')}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cl_lieu" className="text-sm text-gray-700">Lieu de naissance</Label>
              <Input id="cl_lieu" value={formulaire.lieu_naissance} onChange={(e) => changer('lieu_naissance', e.target.value)} maxLength={100} className={CLASSES_CHAMP} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cl_tel" className="text-sm text-gray-700">Téléphone</Label>
              <Input id="cl_tel" value={formulaire.telephone} onChange={(e) => changer('telephone', e.target.value)} maxLength={30} className={CLASSES_CHAMP} />
            </div>
          </div>
          <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-xs text-gray-600">
            Le candidat libre sera créé à l&apos;enregistrement de l&apos;import, avec son résultat, sans frais
            d&apos;examen à payer : on constate un résultat, on ne réclame rien. Il comptera à part, hors du taux de
            réussite de l&apos;école.
          </div>
        </div>

        <DialogFooter className="px-5 py-4 border-t bg-gray-50 sm:justify-end gap-2">
          <Button variant="outline" onClick={onFermer} className={BOUTON_PIED_CLASSES}>Annuler</Button>
          <Button onClick={declarer} className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1f5a7a]`}>
            <CheckCircle size={16} />
            Déclarer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeclarationCandidatLibre;
